/**
 * Input parsing for the settlement screen. Meter values are NOT money — they
 * carry up to three decimals and a Belgian keyboard types them with a comma.
 * Damage IS money and therefore integer euros → cents, no floats anywhere.
 */

export type Standen = Partial<
  Record<"electricity" | "gas" | "water", { arrival?: number; departure?: number }>
>;

/** "41282,5" → 41282.5 · "" → undefined · rubbish → undefined. */
export function parseStand(input: string): number | undefined {
  const genormaliseerd = input.trim().replace(",", ".");
  if (!/^\d{1,9}(\.\d{1,3})?$/.test(genormaliseerd)) return undefined;
  return Number(genormaliseerd);
}

/** Whole euros only: "45" → 4500 cents. Cents never enter as a decimal. */
export function parseSchadeEuros(input: string): number | undefined {
  const genormaliseerd = input.trim();
  if (genormaliseerd === "") return 0;
  if (!/^\d{1,5}$/.test(genormaliseerd)) return undefined;
  return Number(genormaliseerd) * 100;
}

/**
 * Whole people: "27" → 27 · "" → undefined · rubbish → undefined. Capped at
 * the building's capacity of 80, mirroring the API's validation (0.13b).
 */
export function parseAantal(input: string): number | undefined {
  const genormaliseerd = input.trim();
  if (!/^\d{1,2}$/.test(genormaliseerd)) return undefined;
  return Number(genormaliseerd) > 80 ? undefined : Number(genormaliseerd);
}

/** Meter values for display: Belgian decimal comma, no trailing zeros. */
export function formatStand(value: number): string {
  return value.toLocaleString("nl-BE", { maximumFractionDigits: 3 });
}

/**
 * A stored value as INPUT text: decimal comma, NO grouping — "49.945,6" back
 * into the field would be rejected by parseStand as ambiguous, and rightly so.
 */
export function standNaarInvoer(value: number): string {
  return String(value).replace(".", ",");
}
